import { format, isValid, parseISO } from "date-fns";
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_VisibilityState,
} from "material-react-table";

import { ProspectFilterVariant, ProspectTableFieldDefinition } from "./model";
import {
  DEFAULT_SHOW_COLUMN_FILTERS,
  DEFAULT_SHOW_GLOBAL_FILTER,
  DEFAULT_SORTING,
  defaultProspectPageSize,
} from "./tableConfig";

const RANGE_SEPARATOR = "..";

export const FILTER_PREFIX = "filter.";

/**
 * URL search-param keys this codec treats as "view state" — the subset
 * persisted to the staff view-states API alongside any `filter.*` keys.
 * Anything else in the URL (e.g. unrelated query params) is left alone.
 */
export const RECOGNIZED_VIEW_STATE_KEYS = new Set([
  "q",
  "sort",
  "hide",
  "filters",
  "search",
]);

const PAGE_KEY = "page";

export function readPageIndex(params: URLSearchParams): number {
  const page = Number(params.get(PAGE_KEY));
  return Number.isInteger(page) && page > 1 ? page - 1 : 0;
}

export const readPagination = (
  params: URLSearchParams,
): MRT_PaginationState => ({
  pageIndex: readPageIndex(params),
  pageSize: defaultProspectPageSize,
});

export function writePageIndex(params: URLSearchParams, pageIndex: number) {
  if (pageIndex > 0) {
    params.set(PAGE_KEY, String(pageIndex + 1));
  } else {
    params.delete(PAGE_KEY);
  }
}

const isViewStateKey = (key: string): boolean =>
  key.startsWith(FILTER_PREFIX) || RECOGNIZED_VIEW_STATE_KEYS.has(key);

/**
 * `true` if the URL already carries any recognised view-state key. Used at
 * mount time to decide whether the URL should win over server-persisted
 * state (so a shared link is honoured verbatim).
 */
export function hasAnyViewStateParams(params: URLSearchParams): boolean {
  for (const key of params.keys()) {
    if (isViewStateKey(key)) return true;
  }
  return false;
}

/**
 * Pull only the recognised view-state entries out of `params` into a
 * plain object suitable for `JSON.stringify` and storing as the
 * `params` half of the persisted view-state payload.
 */
export function extractViewStateParams(
  params: URLSearchParams,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of params.entries()) {
    if (isViewStateKey(key)) {
      result[key] = value;
    }
  }
  return result;
}

const parseNumberBound = (raw: string): number | string => {
  if (raw === "") return "";
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? raw : parsed;
};

const parseDateBound = (raw: string): Date | "" => {
  if (raw === "") return "";
  const parsed = parseISO(raw);
  return isValid(parsed) ? parsed : "";
};

export function parseRangeValue(
  raw: string,
  type: "number" | "date",
): unknown[] {
  const separatorIndex = raw.indexOf(RANGE_SEPARATOR);
  const rawStart = separatorIndex < 0 ? raw : raw.slice(0, separatorIndex);
  const rawEnd =
    separatorIndex < 0
      ? ""
      : raw.slice(separatorIndex + RANGE_SEPARATOR.length);

  if (type === "number") {
    return [parseNumberBound(rawStart), parseNumberBound(rawEnd)];
  }

  return [parseDateBound(rawStart), parseDateBound(rawEnd)];
}

const isEmptyRange = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.every((entry) => entry === "" || entry === null || entry === undefined);

const formatDateBound = (value: Date) => format(value, "yyyy-MM-dd");

const formatBound = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  if (value instanceof Date) return formatDateBound(value);
  if (typeof value === "number" && Number.isNaN(value)) return "";
  return String(value);
};

/**
 * In-list separator for multi-select filter values. Pipe rather than
 * comma so that scalar option values containing commas — e.g.
 * `"Greater than 5k, less than 10k"` — survive round-trip without
 * special encoding. Percent-encoding of unsafe characters is handled
 * once at the URL boundary by `encodeValueComponent`; this codec keeps
 * raw decoded values inside `URLSearchParams`.
 */
const MULTI_SELECT_SEPARATOR = "|";

export function encodeMultiSelectList(
  values: readonly string[],
): string | null {
  const parts = values
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
  return parts.length ? parts.join(MULTI_SELECT_SEPARATOR) : null;
}

export function decodeMultiSelectList(raw: string): string[] {
  if (!raw) return [];
  return raw
    .split(MULTI_SELECT_SEPARATOR)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

export function formatFilterValue(
  value: unknown,
  variant: ProspectFilterVariant,
): string | null {
  if (value === null || value === undefined) return null;

  if (variant === "numberRange" || variant === "dateRange") {
    if (!Array.isArray(value)) {
      const raw = formatBound(value);
      return raw.length ? raw : null;
    }
    if (isEmptyRange(value)) return null;

    const [start, end] = value;
    return `${formatBound(start)}${RANGE_SEPARATOR}${formatBound(end)}`;
  }

  if (variant === "multiSelect") {
    if (!Array.isArray(value)) return null;
    const stringValues = value.filter(
      (entry): entry is string => typeof entry === "string",
    );
    return encodeMultiSelectList(stringValues);
  }

  if (Array.isArray(value)) {
    const raw = value
      .map(formatBound)
      .filter((entry) => entry.length > 0)
      .join(",");
    return raw.length ? raw : null;
  }

  const raw = formatBound(value);
  return raw.length ? raw : null;
}

export function encodeValueComponent(value: string): string {
  return encodeURIComponent(value).replace(/%2C/g, ",");
}

export function parseSortParam(raw: string | null): MRT_SortingState {
  if (!raw) return DEFAULT_SORTING;
  const desc = raw.startsWith("-");
  const id = desc ? raw.slice(1) : raw;
  if (!id) return DEFAULT_SORTING;
  return [{ id, desc }];
}

export function formatSortParam(sorting: MRT_SortingState): string | null {
  if (!sorting.length) return null;

  const { id, desc } = sorting[0];
  const defaultSort = DEFAULT_SORTING[0];
  if (
    sorting.length === 1 &&
    id === defaultSort.id &&
    desc === defaultSort.desc
  ) {
    return null;
  }

  return desc ? `-${id}` : id;
}

const fieldIndexById = (
  fieldDefinitions: ProspectTableFieldDefinition[],
): Map<string, ProspectTableFieldDefinition> => {
  const map = new Map<string, ProspectTableFieldDefinition>();
  for (const field of fieldDefinitions) {
    map.set(field.field, field);
  }
  return map;
};

/**
 * Serialise URL search params back to a query string with `,` left
 * unencoded — `hide=email,projectDeveloper` reads better than the
 * default `%2C`-encoded form, and routers/servers parse them
 * identically.
 */
export function writeReadableParams(params: URLSearchParams): string {
  return Array.from(params.entries())
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeValueComponent(value)}`,
    )
    .join("&");
}

const sortedQuery = (params: URLSearchParams): string => {
  const copy = new URLSearchParams(params);
  copy.sort();
  return copy.toString();
};

/**
 * Apply a mutation to a copy of `current`, returning the next params if
 * they actually changed, or `null` if they didn't (so the caller can skip
 * a no-op router navigation).
 */
export function buildNextParams(
  current: URLSearchParams,
  apply: (params: URLSearchParams) => void,
): URLSearchParams | null {
  const next = new URLSearchParams(current);
  apply(next);

  return sortedQuery(next) === sortedQuery(current) ? null : next;
}

/**
 * Merge view-state keys saved on the server into the current URL,
 * skipping any key the user typed at mount or has since navigated to.
 * The mount snapshot makes shared/bookmarked links authoritative —
 * server values fill gaps, they don't override.
 *
 * @returns The next `URLSearchParams` if anything was added, otherwise
 *   `null` (caller skips navigation).
 */
export function mergeServerParamsIntoUrl(
  incoming: Record<string, string>,
  mountParams: URLSearchParams,
  currentParams: URLSearchParams,
): URLSearchParams | null {
  const next = new URLSearchParams(currentParams);
  let changed = false;
  for (const [key, value] of Object.entries(incoming)) {
    if (typeof key !== "string" || typeof value !== "string") continue;
    if (mountParams.has(key) || next.has(key)) continue;
    next.set(key, value);
    changed = true;
  }
  return changed ? next : null;
}

/**
 * The set of `filter.<columnId>` keys currently in `params` whose
 * `columnId` matches a known field. Used to clear "our" filter keys
 * before writing a fresh set, while leaving any unknown `filter.*`
 * keys (e.g. for columns this build no longer knows about) intact.
 */
export function knownFilterKeys(
  params: URLSearchParams,
  fieldDefinitions: ProspectTableFieldDefinition[],
): string[] {
  const knownIds = new Set(fieldDefinitions.map((field) => field.field));
  const keys = new Set<string>();

  for (const key of params.keys()) {
    if (!key.startsWith(FILTER_PREFIX)) continue;
    const columnId = key.slice(FILTER_PREFIX.length);
    if (knownIds.has(columnId)) {
      keys.add(key);
    }
  }

  return [...keys];
}

/**
 * Parse all `filter.*` URL keys into MRT's column-filter state shape,
 * dropping unknown columns and empty ranges.
 */
export function readColumnFilters(
  params: URLSearchParams,
  fieldDefinitions: ProspectTableFieldDefinition[],
): MRT_ColumnFiltersState {
  const fields = fieldIndexById(fieldDefinitions);
  const filters: MRT_ColumnFiltersState = [];

  for (const [key, rawValue] of params.entries()) {
    if (!key.startsWith(FILTER_PREFIX)) continue;
    const columnId = key.slice(FILTER_PREFIX.length);
    const field = fields.get(columnId);
    if (!field) continue;

    if (field.filterVariant === "numberRange") {
      const value = rawValue.includes("..")
        ? parseRangeValue(rawValue, "number")
        : rawValue;
      if (isEmptyRange(value)) continue;
      filters.push({ id: columnId, value });
      continue;
    }

    if (field.filterVariant === "dateRange") {
      const value = rawValue.includes("..")
        ? parseRangeValue(rawValue, "date")
        : rawValue;
      if (isEmptyRange(value)) continue;
      filters.push({ id: columnId, value });
      continue;
    }

    if (field.filterVariant === "multiSelect") {
      const value = decodeMultiSelectList(rawValue);
      if (value.length === 0) continue;
      filters.push({ id: columnId, value });
      continue;
    }

    if (rawValue === "") continue;
    filters.push({ id: columnId, value: rawValue });
  }

  return filters;
}

/**
 * Parse the `hide=` URL key into MRT's column-visibility state, only
 * including entries for known columns.
 */
export function readColumnVisibility(
  params: URLSearchParams,
  fieldDefinitions: ProspectTableFieldDefinition[],
): MRT_VisibilityState {
  const rawHide = params.get("hide");
  if (!rawHide) return {};

  const knownIds = new Set(fieldDefinitions.map((field) => field.field));
  const visibility: MRT_VisibilityState = {};
  for (const columnId of rawHide.split(",")) {
    if (knownIds.has(columnId)) {
      visibility[columnId] = false;
    }
  }

  return visibility;
}

export const readShowColumnFilters = (params: URLSearchParams): boolean =>
  params.get("filters") === "off" ? false : DEFAULT_SHOW_COLUMN_FILTERS;

export const readShowGlobalFilter = (params: URLSearchParams): boolean =>
  params.get("search") === "off" ? false : DEFAULT_SHOW_GLOBAL_FILTER;

/**
 * The sorted column ids that should be written into `hide=` for a
 * given visibility state, restricted to currently known columns.
 */
export function hiddenColumnIds(
  columnVisibility: MRT_VisibilityState,
  fieldDefinitions: ProspectTableFieldDefinition[],
): string[] {
  const knownIds = new Set(fieldDefinitions.map((field) => field.field));
  return Object.entries(columnVisibility)
    .filter(
      ([columnId, visible]) => visible === false && knownIds.has(columnId),
    )
    .map(([columnId]) => columnId)
    .sort();
}
